// src/features/assistant-connect/components/AssistantConnectPage.tsx

import React from 'react';
import { Title, useTranslate } from 'react-admin';
import { Navigate, useSearchParams } from 'react-router-dom';
import { Alert, Box, Card, Divider, Stack, Tab, Tabs, Typography, useTheme } from '@mui/material';
import { API_CONFIG } from '@/config/constants';
import { getCurrentRole } from '@/utils/auth';
import { ASSISTANTS, ASSISTANT_CLIENT_IDS, type AssistantId } from '../constants';
import { assistantServerUrl, canConnectAssistant } from '../utils/access';
import { tasksFor } from '../utils/catalog';
import { GUIDE, type GuideSection } from '../utils/guide';
import { describeSurface } from '../utils/surface';
import { CopyableCommand } from './CopyableCommand';

const HOW_POINTS = ['account', 'readOnly', 'consent', 'disconnect'] as const;

const isAssistant = (value: string | null): value is AssistantId =>
  value !== null && (ASSISTANTS as readonly string[]).includes(value);

/** A card with a title row over its content. */
const Section: React.FC<{ title: string; children: React.ReactNode }> = ({ title, children }) => (
  <Card>
    <Typography variant="h6" component="h2" sx={{ px: 2.25, py: 1.5, fontSize: '1rem', fontWeight: 600 }}>
      {title}
    </Typography>
    <Divider />
    <Stack spacing={2} sx={{ p: 2.25, minWidth: 0 }}>
      {children}
    </Stack>
  </Card>
);

/** A labelled paragraph: the label as a caption over the text. */
const Labelled: React.FC<{ label: string; text: string }> = ({ label, text }) => (
  <Box>
    <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
      {label}
    </Typography>
    <Typography variant="body2">{text}</Typography>
  </Box>
);

const SectionSteps: React.FC<{ assistant: AssistantId; section: GuideSection; serverUrl: string }> = ({
  assistant,
  section,
  serverUrl,
}) => {
  const translate = useTranslate();
  const base = `assistantConnect.assistants.${assistant}.sections.${section.key}`;
  const steps = Array.from({ length: section.steps }, (_, index) => index + 1);

  return (
    <Section title={translate(`${base}.title`)}>
      <Labelled label={translate('assistantConnect.requirements')} text={translate(`${base}.requirements`)} />
      <Box component="ol" sx={{ m: 0, pl: 2.5, display: 'grid', rowGap: 1.25 }}>
        {steps.map((step) => (
          <Typography key={step} component="li" variant="body2" sx={{ minWidth: 0 }}>
            {translate(`${base}.step${step}`)}
            {section.block?.afterStep === step && (
              <Box sx={{ mt: 1 }}>
                <CopyableCommand value={section.block.value(serverUrl)} />
              </Box>
            )}
          </Typography>
        ))}
      </Box>
      {section.note && (
        <Typography variant="caption" color="text.secondary">
          {translate(`${base}.note`)}
        </Typography>
      )}
    </Section>
  );
};

const AssistantTab: React.FC<{ assistant: AssistantId; serverUrl: string }> = ({ assistant, serverUrl }) => {
  const translate = useTranslate();

  return (
    <Stack spacing={2}>
      <Section title={translate('assistantConnect.connection.title')}>
        <CopyableCommand label={translate('assistantConnect.connection.serverUrl')} value={serverUrl} />
        <CopyableCommand
          label={translate('assistantConnect.connection.clientId')}
          value={ASSISTANT_CLIENT_IDS[assistant]}
          hint={translate('assistantConnect.connection.secret')}
        />
      </Section>

      {GUIDE[assistant].map((section) => (
        <SectionSteps key={section.key} assistant={assistant} section={section} serverUrl={serverUrl} />
      ))}

      <Labelled
        label={translate('assistantConnect.disconnectLabel')}
        text={translate(`assistantConnect.assistants.${assistant}.disconnect`)}
      />
    </Stack>
  );
};

/**
 * «Conecte su asistente de IA»: how a person connects the AI assistant they already use to Insight.
 * The assistant signs in as that person and the server forwards their own session, so the page
 * promises nothing the person cannot already see here. Opened from the app bar; a profile the
 * server refuses is sent to the not-authorized page.
 */
export const AssistantConnectPage: React.FC = () => {
  const translate = useTranslate();
  const theme = useTheme();
  const [params, setParams] = useSearchParams();
  const role = getCurrentRole();
  const surface = describeSurface(API_CONFIG.BASE_URL);
  const serverUrl = assistantServerUrl(surface.environment);

  if (!canConnectAssistant(role)) return <Navigate to="/not-authorized" replace />;

  const requested = params.get('assistant');
  const assistant: AssistantId = isAssistant(requested) ? requested : 'claude';
  const tasks = tasksFor(role);

  return (
    <Box sx={{ maxWidth: 960, minWidth: 0 }}>
      <Title title={translate('assistantConnect.name')} />
      <Box sx={{ mb: 2 }}>
        <Typography variant="h5" component="h1" sx={{ fontWeight: 600 }}>
          {translate('assistantConnect.title')}
        </Typography>
        <Typography variant="body2" sx={{ color: theme.custom.text.tertiary, mt: 0.5 }}>
          {translate('assistantConnect.subtitle')}
        </Typography>
      </Box>

      <Stack spacing={2}>
        <Section title={translate('assistantConnect.how.title')}>
          <Box component="ul" sx={{ m: 0, pl: 2.5, display: 'grid', rowGap: 0.75 }}>
            {HOW_POINTS.map((point) => (
              <Typography key={point} component="li" variant="body2">
                {translate(`assistantConnect.how.${point}`)}
              </Typography>
            ))}
          </Box>
          {surface.environment !== 'production' && (
            <Alert severity="info" variant="outlined" sx={{ alignItems: 'center' }}>
              {translate(
                surface.environment === 'unknown' ? 'assistantConnect.environment.unknown' : 'assistantConnect.environment.test'
              )}
            </Alert>
          )}
        </Section>

        <Box sx={{ minWidth: 0 }}>
          <Tabs
            value={assistant}
            onChange={(_, value: AssistantId) => setParams({ assistant: value }, { replace: true })}
            aria-label={translate('assistantConnect.tabsLabel')}
            variant="scrollable"
            scrollButtons="auto"
            allowScrollButtonsMobile
            sx={{ mb: 2, borderBottom: 1, borderColor: 'divider' }}
          >
            {ASSISTANTS.map((id) => (
              <Tab
                key={id}
                value={id}
                id={`assistant-tab-${id}`}
                aria-controls={`assistant-panel-${id}`}
                sx={{ minHeight: 48 }}
                label={translate(`assistantConnect.assistants.${id}.name`)}
              />
            ))}
          </Tabs>

          <Box role="tabpanel" id={`assistant-panel-${assistant}`} aria-labelledby={`assistant-tab-${assistant}`}>
            <AssistantTab assistant={assistant} serverUrl={serverUrl} />
          </Box>
        </Box>

        <Section title={translate('assistantConnect.tasks.title')}>
          <Typography variant="body2" color="text.secondary">
            {translate('assistantConnect.tasks.subtitle')}
          </Typography>
          <Box component="ul" sx={{ m: 0, pl: 2.5, display: 'grid', rowGap: 1 }}>
            {tasks.map(({ key }) => (
              <Box component="li" key={key} sx={{ minWidth: 0 }}>
                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                  {translate(`assistantConnect.tasks.items.${key}.title`)}
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                  {translate(`assistantConnect.tasks.items.${key}.summary`)}
                </Typography>
              </Box>
            ))}
          </Box>
        </Section>
      </Stack>
    </Box>
  );
};

export default AssistantConnectPage;
